<script setup>
import { computed, ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import SidenavItem from "./SidenavItem.vue";
import { isAkunDemo } from "@/utils/role-utils";

const store = useStore();
const route = useRoute();

// Get role from Vuex store (secure - fetched from API)
const role = computed(() => store.getters['auth/userRole']);

// Check if user has role to see transfer button (Finance or Admin)
const hasTransferRole = computed(() => ['Finance', 'Admin'].includes(role.value));

// Fetch user profile on mount to ensure role is loaded
onMounted(async () => {
  // Only fetch if role is not already in store
  if (!store.getters['auth/userRole']) {
    await store.dispatch('auth/fetchUserProfile');
  }
});

// menu master list
const menuItems = ref([
  {
    name: "Home",
    to: "/home",
    key: "home",
    icon: "/assets/icons/ic_home.png",
    allowedRoles: ["Admin", "IT", "Finance", "Staff"],
  },
  {
    name: "Balance",
    to: "/balance",
    key: "balance",
    icon: "/assets/icons/ic_wallet.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Transaction",
    to: "/transaction",
    key: "transaction",
    icon: "/assets/icons/ic_report.png",
    allowedRoles: ["Admin", "Finance", "Staff"],
  },
  {
    name: "Integrations",
    to: "/integrations",
    key: "integrations",
    icon: "/assets/icons/ic_integration.svg",
    allowedRoles: ["Admin", "IT"],
  },
  {
    name: "User Management",
    to: "/user-management",
    key: "user-management",
    icon: "/assets/icons/ic_user_management.svg",
    activeIcon: "/assets/icons/ic_user_management_active.svg",
    allowedRoles: ["Admin"],
  },
  {
    name: "Help",
    to: "/help-center",
    key: "help-center",
    icon: "/assets/icons/ic_help.png",
    allowedRoles: ["Admin", "IT", "Finance", "Staff"],
  },
]);

// filter berdasarkan role (using computed role from store)
// dan hide Balance & Integrations menu jika company_name === 'AKUN DEMO'
const filteredMenu = computed(() => {
  const isDemoAccount = isAkunDemo();
  return menuItems.value.filter((item) => {
    // Hide Balance and Integrations menu if company_name === 'AKUN DEMO'
    if ((item.key === 'balance' || item.key === 'integrations' || item.key === 'user-management') && isDemoAccount) {
      return false;
    }
    return item.allowedRoles.includes(role.value);
  });
});

const getCurrentRoute = computed(() => {
  return route.path.split("/")[1];
});
</script>

<template>
  <aside class="sidebar fixed-start" id="sidenav-main">
    <div class="sidebar-body">
      <ul class="nav-list">
        <!-- Transfer button - show if user has Finance/Admin role -->
        <!-- Router guard will redirect to Forbidden if KYB not approved -->
        <li
          class="p-4 transfer-wrapper"
          v-if="hasTransferRole"
        >
          <button
            class="w-100 transfer-button transfer-text"
            @click="$router.push('/transfer')"
          >
            Transfer
            <img
              src="/assets/ic_send.png"
              alt="Send Icon"
              style="width: 24px; height: 24px; margin-left: 8px"
            />
          </button>
        </li>

        <hr />

        <!-- Loop menu items -->
        <li
          v-for="item in filteredMenu"
          :key="item.key"
          :class="{ active: getCurrentRoute === item.key }"
        >
          <sidenav-item :to="item.to" :icon="item.key">
            <template v-slot:icon>
              <img
                :src="
                  item.key === 'user-management' &&
                  getCurrentRoute === 'user-management'
                    ? item.activeIcon
                    : item.icon
                "
                alt=""
                width="24"
                height="24"
              />
            </template>
            {{ item.name }}
          </sidenav-item>
        </li>
      </ul>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 250px;
  height: calc(100vh - 56px);
  margin-top: 56px;
  background-color: #fff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  /* position: fixed; */
  top: 56px;
  /* left: 0; */
  /* z-index: 999; */
}

.sidebar-header {
  padding: 20px 16px;
  border-bottom: 1px solid #e9ecef;
}

.logo-container {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo {
  margin-right: 10px;
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
  color: #4caf50;
}

.sidebar-body {
  flex: 1;
  padding: 16px 0;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 10px;
}

.nav-list li {
  margin-bottom: 4px;
}

.nav-list li.active {
  background-color: #e9fbf0;
  border-radius: 100px;
  color: #128050;
  font-weight: 600;
  text-decoration: none;
}

.transfer-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  border-radius: 100px;
  border: none;
  padding: 8px 16px 8px 24px;
  background-color: #43bb75;
}

.transfer-text {
  font-family: "IBM Plex Sans Devanagari";
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  line-height: 150%;
  letter-spacing: 0%;
  vertical-align: middle;
}

.transfer-wrapper {
  position: relative;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.badge-maintenance {
  position: absolute;
  top: 8px;
  right: 12px;
  background-color: orange;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: bold;
  z-index: 10;
  box-shadow: 0 0 4px rgba(220, 53, 69, 0.6);
  animation: pulse 1.5s infinite ease-in-out;
}
</style>
